import { BlockType } from '../../../models/enums';
import { getSelectedRange, setCursorPosition } from '../../../common/utils/selection';
import { getBlockContentElement, getBlockModelById, isListTypeBlock } from '../../../common/utils/block';
import * as constants from '../../../common/constant';
export class ListPlugin {
    constructor(manager) {
        this.parent = manager;
    }
    /**
     * Handles the key press event for list blocks.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     * @param {HTMLElement} blockElement - The block element.
     * @returns {boolean} - Returns true if the event is handled.
     * @hidden
     */
    handleListKeyActions(event, blockElement) {
        const range = getSelectedRange();
        const blockModel = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
        let isActionProcessed = false;
        switch (event.key) {
            case 'Enter':
                this.handleEnterKey(event, blockElement, range, blockModel);
                this.parent.isEntireEditorSelected = false;
                isActionProcessed = true;
                break;
            case 'Backspace':
                this.handleBackspaceKey(event, blockElement, range, blockModel);
                this.parent.isEntireEditorSelected = false;
                isActionProcessed = true;
                break;
        }
        return isActionProcessed;
    }
    /**
     * Handles creation of new list item on particular key triggers
     *
     * @param {KeyboardEvent} event - The keyboard event.
     * @param {HTMLElement} blockElement - The block element.
     * @param {BlockModel} blockModel - The block model.
     * @returns {void}
     * @hidden
     */
    handleListTriggerKey(event, blockElement, blockModel) {
        /**
         * Below are the list triggers for the block editor.
         * (*) or (-) for bullet list.
         * (1.) for numbered list.
         * ([]) for checklist.
         */
        const content = blockElement.textContent.trim();
        if (content.length <= 0) {
            return;
        }
        const validLength = (content.startsWith('*') || content.startsWith('-')) ? 1 : 2;
        const isListTrigger = content.startsWith('*') ||
            content.startsWith('-') ||
            content.startsWith('1.') ||
            content.startsWith('[]');
        if (isListTrigger && event.key === ' ' && event.code === 'Space' && content.length === validLength) {
            event.preventDefault();
            let listType;
            switch (content) {
                case '*':
                case '-':
                    listType = BlockType.BulletList.toString();
                    break;
                case '1.':
                    listType = BlockType.NumberedList.toString();
                    break;
                case '[]':
                    listType = BlockType.Checklist.toString();
                    break;
            }
            this.transformBlockToList(blockElement, blockModel, listType);
        }
    }
    handleEnterKey(event, blockElement, range, blockModel) {
        event.preventDefault();
        const isEmpty = blockElement.textContent.trim() === '';
        if (isEmpty) {
            if (blockModel.indent > 0) {
                this.parent.execCommand({ command: 'IndentBlock', state: {
                        blockIDs: [blockModel.id],
                        shouldDecrease: true
                    } });
            }
            else {
                this.parent.blockCommand.transformBlockToParagraph(blockElement, blockModel);
            }
        }
        else {
            this.parent.execCommand({ command: 'SplitBlock' });
        }
    }
    handleBackspaceKey(event, blockElement, range, blockModel) {
        const isAtStart = range.collapsed && range.startOffset === 0;
        const isEmpty = blockElement.textContent.trim() === '';
        if (!isAtStart && !isEmpty) {
            return;
        }
        event.preventDefault();
        this.parent.blockCommand.transformBlockToParagraph(blockElement, blockModel);
        this.recalculateMarkersForListItems();
    }
    transformBlockToList(blockElement, blockModel, listType) {
        if (blockModel.content.length > 0) {
            blockModel.content[0].content = '';
        }
        const newBlockElement = this.parent.blockCommand.transformBlock({
            block: blockModel,
            blockElement: blockElement,
            newBlockType: listType
        });
        const contentElement = getBlockContentElement(newBlockElement);
        setCursorPosition(contentElement, 0);
        this.updateListItemMarkers(newBlockElement);
    }
    /**
     * Updates the list item markers the given block element.
     *
     * @param {HTMLElement} blockElement - The block element to update.
     * @returns {void}
     * @hidden
     */
    updateListItemMarkers(blockElement) {
        const prevBlockType = blockElement.getAttribute('data-block-type');
        const listItem = blockElement.querySelector('li');
        const isNumbered = prevBlockType === BlockType.NumberedList;
        const isChecklist = prevBlockType === BlockType.Checklist;
        if (isChecklist || !isNumbered) {
            return;
        }
        const index = this.getNumberedListItemIndex(blockElement);
        const indentLevel = this.getIndentLevel(blockElement);
        const marker = this.getListMarker(index, indentLevel);
        listItem.style.setProperty('list-style-type', `"${marker} "`);
    }
    getNumberedListItemIndex(blockElement) {
        let index = 1;
        const allBlocks = this.getAllBlockElements(blockElement);
        const currentBlockIndex = allBlocks.indexOf(blockElement);
        if (currentBlockIndex < 0) {
            return index;
        }
        const currentIndentLevel = this.getIndentLevel(blockElement);
        // Count only blocks with same indent level and same type starting from current block
        for (let i = currentBlockIndex - 1; i >= 0; i--) {
            const prevBlock = allBlocks[i];
            const prevBlockType = prevBlock.getAttribute('data-block-type');
            const prevIndentLevel = this.getIndentLevel(prevBlock);
            if (prevBlockType !== BlockType.NumberedList || currentIndentLevel > prevIndentLevel) {
                break; // Stop when block type changes or indent level increases(new series)
            }
            if (prevIndentLevel === currentIndentLevel) {
                index++; // Increment only when indent level is same
            }
        }
        return index;
    }
    getIndentLevel(blockElement) {
        return parseInt((blockElement.style.getPropertyValue(constants.INDENT_KEY)), 10) / ListPlugin.INDENT_STEP_SIZE;
    }
    getListMarker(index, indentLevel) {
        const getRomanNumeral = (num) => {
            // Use lookup table for numbers 1–20 (Improved efficiency)
            if (num >= 1 && num <= 20) {
                return ListPlugin.ROMANNUMERALLOOKUP[num];
            }
            // Fallback algorithm for numbers >20
            const romanMap = [
                [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
                [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']
            ];
            let result = '';
            for (const [value, symbol] of romanMap) {
                while (num >= value) {
                    result += symbol;
                    num -= value;
                }
            }
            return result;
        };
        const getLetterSequence = (num) => {
            let result = '';
            while (num > 0) {
                num--;
                result = String.fromCharCode(97 + (num % 26)) + result;
                num = Math.floor(num / 26);
            }
            return result;
        };
        switch (indentLevel % 3) {
            case 0:
                return `${index}.`;
            case 1:
                return `${getLetterSequence(index)}.`;
            case 2:
                return `${getRomanNumeral(index)}.`;
            default:
                return `${index}.`;
        }
    }
    /**
     * Recalculate the markers for all list items in the editor.
     *
     * @returns {void}
     * @hidden
     */
    recalculateMarkersForListItems() {
        const allBlocks = this.getAllBlockElements();
        const indexByIndent = new Map();
        for (const block of allBlocks) {
            const blockType = block.getAttribute('data-block-type');
            if (!isListTypeBlock(blockType) || blockType === BlockType.Checklist || blockType === BlockType.BulletList) {
                continue;
            }
            const indentLevel = this.getIndentLevel(block);
            const prevBlockIndex = allBlocks.indexOf(block) - 1;
            const prevBlock = prevBlockIndex >= 0 ? allBlocks[prevBlockIndex] : null;
            const prevBlockType = prevBlock ? prevBlock.getAttribute('data-block-type') : '';
            const prevIndentLevel = prevBlock ? this.getIndentLevel(prevBlock) : -1;
            // Reset counters for deeper levels when we encounter a shallower or equal level
            if (prevBlock && (prevIndentLevel > indentLevel ||
                (prevIndentLevel === indentLevel && prevBlockType !== BlockType.NumberedList))) {
                for (const [level] of indexByIndent) {
                    if (level > indentLevel) {
                        indexByIndent.delete(level);
                    }
                }
            }
            if (prevBlockType !== BlockType.NumberedList || prevIndentLevel < indentLevel) {
                indexByIndent.set(indentLevel, 1); // Start new sequence
            }
            else {
                // prevIndentLevel > indentLevel - returning to shallower level
                const currentIndex = (indexByIndent.get(indentLevel) || 0) + 1;
                indexByIndent.set(indentLevel, currentIndex);
            }
            const index = indexByIndent.get(indentLevel) || 1;
            this.setNumberedListMarker(block, index, indentLevel);
        }
    }
    setNumberedListMarker(blockElement, index, indent) {
        const listItem = blockElement.querySelector('li');
        const marker = this.getListMarker(index, indent);
        listItem.style.setProperty('list-style-type', `"${marker} "`);
    }
    getAllBlockElements(focusedBlock) {
        const currentElement = focusedBlock || this.parent.currentFocusedBlock;
        const calloutBlock = currentElement &&
            currentElement.closest('.' + constants.CALLOUT_BLOCK_CLS);
        const quoteBlock = currentElement &&
            currentElement.closest('.' + constants.QUOTE_BLOCK_CLS);
        const toggleBlock = currentElement &&
            currentElement.closest('.' + constants.TOGGLE_BLOCK_CLS);
        const tableBlock = currentElement &&
            currentElement.closest('.' + constants.TABLE_BLOCK_CLS);
        const parentBlock = calloutBlock || quoteBlock || toggleBlock || tableBlock;
        const allBlocks = parentBlock
            ? Array.from(parentBlock.querySelectorAll('.' + constants.BLOCK_CLS))
            : Array.from(this.parent.blockContainer.children);
        return allBlocks;
    }
}
ListPlugin.INDENT_STEP_SIZE = 20;
// Lookup table for Roman numerals 1–20
ListPlugin.ROMANNUMERALLOOKUP = {
    1: 'i', 2: 'ii', 3: 'iii', 4: 'iv', 5: 'v',
    6: 'vi', 7: 'vii', 8: 'viii', 9: 'ix', 10: 'x',
    11: 'xi', 12: 'xii', 13: 'xiii', 14: 'xiv', 15: 'xv',
    16: 'xvi', 17: 'xvii', 18: 'xviii', 19: 'xix', 20: 'xx'
};
